/**
 * rrhhYears.js — Helper único para resolver el rango de años visibles en RRHH.
 *
 * Fuente de verdad: configuración global `rrhh_config` (singleton),
 * accesible vía `GET /api/rrhh/year-range` o leyendo `rrhh_config`:
 *   - `year_range_start_year`   (opcional, "año inicial fijo")
 *   - `year_range_future_years` (default 10)
 *
 * Reglas:
 *   - start = year_range_start_year || (año actual - 2)
 *   - end   = año actual + (year_range_future_years || 10)
 *   - years = [start, start+1, ..., end]  (orden ascendente)
 */

const DEFAULT_FUTURE = 10;
const DEFAULT_BACK_OFFSET = 2;

function _normalizeFuture(future) {
  const n = parseInt(future, 10);
  return Number.isFinite(n) && n >= 0 ? n : DEFAULT_FUTURE;
}

/**
 * Devuelve { start_year, end_year, years } a partir de `rrhh_config`.
 * Acepta tanto el objeto completo de `rrhh_config` como la respuesta de
 * `/api/rrhh/year-range` (que ya trae `years`).
 */
export function getCurrentRrhhYearRange(config) {
  // Si el caller pasa directamente la respuesta del endpoint /year-range
  if (config && Array.isArray(config.years) && config.years.length > 0) {
    return {
      start_year: config.start_year ?? config.years[0],
      end_year: config.end_year ?? config.years[config.years.length - 1],
      years: config.years,
    };
  }

  const current = new Date().getFullYear();
  const future = _normalizeFuture(config?.year_range_future_years);
  let start = parseInt(config?.year_range_start_year, 10);
  if (!Number.isFinite(start) || start <= 0) {
    start = current - DEFAULT_BACK_OFFSET;
  }
  let end = current + future;
  if (end < start) end = start;

  const years = [];
  for (let y = start; y <= end; y += 1) years.push(y);
  return { start_year: start, end_year: end, years };
}

/** Devuelve solo el array de años (orden ascendente). */
export function getVisibleYears(config) {
  return getCurrentRrhhYearRange(config).years;
}

/** Devuelve los años visibles en orden DESCENDENTE (útil para dropdowns
 *  de descarga histórica donde "el más reciente arriba"). */
export function getVisibleYearsDesc(config) {
  return [...getVisibleYears(config)].sort((a, b) => b - a);
}
